/* eslint-disable react/forbid-prop-types */
/* eslint-disable-next-line max-len */
import React, { useState, useEffect, useRef } from 'react';
import { Row, Button, } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import ResizableDataTable from '../../Tables/ResizableTable/components/ResizableReactTable';
import CreateTableData from './CreateTableData';
import { setCurrencyFormat } from '../../../utils/helpers';
import { sortBySubscribersOptions, sortType } from '../helper';

const excelColumns = [
    {
        Header: 'Promotion ID',
        accessor: 'promotionID',
        sortType
    },
    {
        Header: 'Year',
        accessor: 'year',
        sortType
    },
    {
        Header: 'Month',
        accessor: 'month',
        sortType
    },
    {
        Header: 'Quantity',
        accessor: 'totalQty',
        sortType
    },
    {
        Header: 'Amount',
        accessor: 'totalAmount',
        sortType
    },
    {
        Header: 'Sales Channel',
        accessor: 'salesChannel',
        sortType
    },
]

const PromotionTable = ({ dataToShowForDatesSelected,
    tableHeaderData, categorisedBasedOn }) => {
    const { t } = useTranslation('common');
    const categorisedBasedOnRef = useRef({ categorisedBasedOn });

    const [selectedOptions, setSelectedOptions] = useState([])
    const [sortBySelected, setSortBySelected] = useState(sortBySubscribersOptions[1])
    const allCategories = {}
    const currentDataByCategory = {};

    useEffect(() => {
        if (categorisedBasedOnRef?.current?.categorisedBasedOn !== categorisedBasedOn) {
            setSelectedOptions([])
        }
    }, [categorisedBasedOn])

    const reactTableData = CreateTableData();
    const changeFilter = (value) => {
        setSelectedOptions(value)
    };

    dataToShowForDatesSelected.forEach((item) => {
        if (item && currentDataByCategory[item?.[categorisedBasedOn]]) {
            currentDataByCategory[item?.[categorisedBasedOn]]?.push(item)
        } else if (item) {
            if (!item?.[categorisedBasedOn] || item?.[categorisedBasedOn] === 'null') {
                currentDataByCategory?.['N/A']?.push(item)
            } else {
                currentDataByCategory[item?.[categorisedBasedOn]] = [item]
            }
        }
    });

    const currentMonthCat = Object.keys(currentDataByCategory);
    const dataToPlot = [];
    const excelData = [];
    const options = [];
    const reduceCallback = (acc, day) => {

        const curDate = day.startDate;
        if (acc[curDate]) {
            acc?.[curDate]?.push(day)
        } else {
            acc[curDate] = [day]
        }

        if (day && !allCategories[day?.[categorisedBasedOn]]) {
            if (!day?.[categorisedBasedOn] || day?.[categorisedBasedOn] === 'null') {
                allCategories['N/A'] = 'N/A';
            } else {
                allCategories[day?.[categorisedBasedOn]] = day?.[categorisedBasedOn]
            }
        }
        return acc
    }

    for (let i = 0; i < currentMonthCat.length; i += 1) {
        const cat = currentMonthCat[i];
        const currentMonth = currentDataByCategory?.[cat] ?? [];

        const Amount = currentMonth?.reduce((accum, item) =>
            Number(Number(Number(accum) + Number(item.amount)).toFixed(2)), 0);
        const Qty = currentMonth?.reduce((accum, item) =>
            Number(Number(Number(accum) + Number(item.quantity)).toFixed(2)), 0);
        const curYear = currentMonth?.[0]?.year;
        const monthStr = currentMonth?.[0]?.monthStr;
        const SalesChannel = currentMonth?.[0]?.salesChannel;

        options.push({
            value: cat, label: cat
        })

        excelData.push({
            promotionID: cat,
            year: curYear,
            month: `${monthStr}`,
            totalQty: setCurrencyFormat(Qty, false),
            totalAmount: setCurrencyFormat(Amount, true),
            salesChannel: `${SalesChannel}`
        })

        dataToPlot.push({
            promotionID: cat,
            totalQty: Qty,
            totalAmount: Amount,
        })
    }

    // Excel export function using XLSX
    const exportToExcel = () => {
        if (!excelData || !excelData.length) {
            console.log('No data to export');
            return;
        }

        // Prepare data for export using the same structure as excelColumns
        const worksheetData = excelData.map(row => {
            const exportRow = {};
            excelColumns.forEach(col => {
                exportRow[col.Header] = row[col.accessor] || '';
            });
            return exportRow;
        });

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);

        // Set column widths for better formatting
        const columnWidths = excelColumns.map((col) => {
            // Set different widths based on content
            if (col.accessor === 'promotionID') return { wch: 20 };
            if (col.accessor === 'salesChannel') return { wch: 18 };
            if (col.accessor === 'totalAmount') return { wch: 15 };
            if (col.accessor === 'totalQty') return { wch: 12 };
            return { wch: 10 };
        });
        worksheet['!cols'] = columnWidths;

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "report");

        // Save file with same filename as before
        XLSX.writeFile(workbook, "Profit Report.xlsx");
    };

    return (
        <Row style={{ position: 'relative' }}>
            <div className="ExcelTable" id='dvTable'>
                <Button
                    className="download-profit-report btn-primary btn btn-sm"
                    onClick={exportToExcel}
                    disabled={!excelData.length}
                    style={{
                        cursor: !excelData.length ? 'not-allowed' : 'pointer',
                        opacity: !excelData.length ? 0.6 : 1
                    }}
                >
                    Download
                </Button>
            </div>
            <ResizableDataTable customClass="pt-0" reactTableData={{ tableHeaderData }} data={dataToPlot} />
        </Row>
    )
}

PromotionTable.propTypes = {
    dataToShowForDatesSelected: PropTypes.array.isRequired,
    tableHeaderData: PropTypes.array.isRequired,
    categorisedBasedOn: PropTypes.string,
};

PromotionTable.defaultProps = {
    categorisedBasedOn: 'promotionId',
}

export default PromotionTable;